'use client';

import { useCallback, useEffect, useMemo } from 'react';
import { getGiftCards } from 'actions/client';

import { StackProps, VStack } from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectDepositGiftCards } from 'store/reducers/bookingAppointmentSlice';

import { GiftCardInterface } from 'types';

import { PaymentGiftCardItem } from './components';

const GiftCardPaymentList = (props: StackProps) => {
  const { giftCards } = useAppSelector((state) => state.client);
  const { selectedDepositGiftCards, depositAmount } = useAppSelector(
    (state) => state.bookingAppointment
  );
  const dispatch = useAppDispatch();

  const isInitialState = giftCards?.length === 0;

  useEffect(() => {
    if (isInitialState) {
      dispatch(getGiftCards());
    }
  }, [dispatch, isInitialState]);

  const selectedIds = useMemo(
    () => (selectedDepositGiftCards ?? []).map(({ id }) => id),
    [selectedDepositGiftCards]
  );
  const giftCardAmount = useMemo(
    () =>
      (selectedDepositGiftCards ?? []).reduce(
        (acc, card) => acc + card.amt_balance,
        0
      ),
    [selectedDepositGiftCards]
  );

  const onSelectCard = (card: GiftCardInterface) => () => {
    const sum =
      giftCardAmount > depositAmount
        ? depositAmount
        : card.amt_balance - depositAmount;

    if (sum > 0 || depositAmount > 0 || selectedIds.includes(card.id)) {
      dispatch(selectDepositGiftCards(card));
    }
  };

  const getAppliedSum = useCallback(
    (currentId: number) =>
      selectedDepositGiftCards.find(({ id }) => id === currentId),
    [selectedDepositGiftCards]
  );

  return (
    <VStack gap='5px' {...props} w='full'>
      {giftCards.map((giftCard) => (
        <PaymentGiftCardItem
          key={giftCard.id}
          card={giftCard}
          onSelectCard={onSelectCard(giftCard)}
          isActive={selectedIds.includes(giftCard.id)}
          applied={getAppliedSum(giftCard.id)?.amt_balance ?? 0}
        />
      ))}
    </VStack>
  );
};

export default GiftCardPaymentList;
